import MonitorEye from '@mdi/svg/svg/monitor-eye.svg?react';
import Phone from '@mdi/svg/svg/phone-outline.svg?react';
import { type CustomerId } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { Button, MDIIcon, SolinkLogo, usePrevious } from '@solinkcorp/solink-components';
import { type IEvent } from 'clients/PlatformApi';
import useOpenAlarmEvents from 'components/AlarmsPage/AlarmEvents/useOpenAlarmEvents';
import { VerifEyePhoneModal } from 'components/AlarmsPage/AlarmEvents/VerifEyePhoneModal';
import MyAccountPage from 'components/SettingsPage/MyAccountPage';
import 'components/SettingsPage/SettingsPage.sass';
import CenteredLoadingSpinner from 'loadingIndicator/CenteredLoadingSpinner';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAlarmEventsFilter } from 'src/hooks/useAlarmEventsFilter';
import useLocale from 'src/hooks/useLocale';
import chime from '../../assets/chime-sound.wav';
import useVerifEyePhoneModal from '../AlarmsPage/AlarmEvents/useVerifEyePhoneModal';
import UserDropDownMenu from '../NavigationFrame/UserDropDownMenu/UserDropDownMenu';
import useAlarmEditEvent from './useAlarmEditEvent';
import styles from './VerifEyeLandingPage.module.sass';

function VerifEyeLandingPage() {
  const user = useUserSession();
  const routeLocation = useLocation();
  const navigate = useNavigate();
  const locale = useLocale();
  const route = routeLocation.pathname;

  const verifEyePhoneModalProps = useVerifEyePhoneModal();
  const { showVerifEyePhoneModal, setVerifEyePhoneModalOpen } = verifEyePhoneModalProps;

  const openAlarmEventsFilter = useAlarmEventsFilter({
    route,
  });

  const { isLoading, openAlarmsCount, data } = useOpenAlarmEvents({
    jwt: user.accessToken,
    fetchingDisabled: false,
    filter: openAlarmEventsFilter,
  });

  const previousOpenAlarmsCount = usePrevious(openAlarmsCount) || 0;
  const editAlarmEvent = useAlarmEditEvent();

  useEffect(() => {
    if (openAlarmsCount > previousOpenAlarmsCount) {
      new Audio(chime).play();
    }
  }, [openAlarmsCount, previousOpenAlarmsCount]);

  const logout = React.useCallback(() => {
    navigate('/logout');
  }, [navigate]);

  const verifyAlarm = React.useCallback(
    async (event: IEvent) => {
      const updatedEvent = await editAlarmEvent(
        event.id,
        event.customerId! as CustomerId,
        event.siteId,
        {},
        'alarms',
      );
      navigate(`/verification/alarm/${updatedEvent.id}`, {
        state: {
          initialVerificationState: updatedEvent.details?.verificationState,
          verifier: updatedEvent.details?.verifier,
        },
      });
    },
    [editAlarmEvent, navigate],
  );

  const isOnSettingsPage = routeLocation.pathname.startsWith('/settings');
  const getUserMenuItems = React.useMemo(
    () => [
      {
        type: 'label' as const,
        text: user.profile.email || '',
      },
      {
        type: 'divider' as const,
      },
      {
        type: 'action' as const,
        text: locale.navigation.settings,
        onClick: () => navigate('/settings'),
      },
      {
        type: 'action' as const,
        text: locale.auth.logout,
        onClick: logout,
      },
    ],
    [user.profile.email, locale, logout, navigate],
  );

  const header = (
    <div className={styles.header}>
      <div className={styles.leftSection}>
        <div className={styles.companyLogo} data-testid="desk-nav-company-logo">
          <SolinkLogo />
        </div>
      </div>
      <div className={styles.centerSection}>
        <div className={styles.title}>Solink VerifEye</div>
      </div>
      <div className={styles.rightSection}>
        {showVerifEyePhoneModal && (
          <Button
            onClick={() => setVerifEyePhoneModalOpen(true)}
            text={locale.verificationCentre.myVerifEyeNumber}
            icon={<MDIIcon icon={Phone} />}
            colorMode="line-dark"
            typography="capitalize"
          />
        )}
        <UserDropDownMenu
          isImpersonating={false}
          menuItems={getUserMenuItems}
          userName={user.profile.name}
          userEmail={user.profile.email}
        />
      </div>
    </div>
  );

  if (isOnSettingsPage) {
    return (
      <div className={styles.openAlarmEvents}>
        {header}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <nav className={styles.verifEyeNav}>
            <button className={styles.verifEyeNavTab} onClick={() => navigate('/')}>
              <MDIIcon icon={MonitorEye} />
              <span>{locale.navigation.verifeye}</span>
            </button>
          </nav>
          <div className={`settings ${styles.myAccoutPage}`}>
            <MyAccountPage />
          </div>
        </div>
        {showVerifEyePhoneModal && <VerifEyePhoneModal {...verifEyePhoneModalProps} />}
      </div>
    );
  }

  if (isLoading) {
    return <CenteredLoadingSpinner message={locale.verificationCentre.loadingAlarmEvent} />;
  }

  return (
    <div className={styles.openAlarmEvents}>
      {header}
      {/* Navigation Tabs */}
      <div className={styles.navigationTabs}>
        <Button text="Open Alarms" typography="capitalize" />
        <Button
          text="Active Dispatch"
          typography="capitalize"
          className={styles.activeDispatchButton}
        />
      </div>

      {/* Main Content Area */}
      <div className={styles.mainContent}>
        <div className={styles.alarmCountSection}>
          <div className={styles.alarmCount}>{openAlarmsCount}</div>
          <div className={styles.modeInfo}>Open Alarms</div>
          {data?.results && data.results.length > 0 && (
            <div className={styles.buttonWrapper}>
              <Button
                dataTestId="verify-alarms"
                text={`${locale.alarms.verify} ${locale.alarms.alarms}`}
                onClick={() => verifyAlarm(data.results[0])}
                typography="capitalize"
              />
            </div>
          )}
        </div>
      </div>
      {showVerifEyePhoneModal && <VerifEyePhoneModal {...verifEyePhoneModalProps} />}
    </div>
  );
}

export default VerifEyeLandingPage;
